/**
 * Черновик метки в браузере (28.09.2026).
 *
 * Инцидент: Катя 2:56 говорила с клиентом (назначение 16453), начала
 * заполнять заявку — и в 08:56 страница пульта перезагрузилась. Анкета жила
 * только в памяти React и пропала, запрос метки так и не ушёл. Теперь всё
 * введённое в блоке результата пишется в localStorage с ключом «диспетчер +
 * назначение» и возвращается, когда эта карточка снова на экране: после F5,
 * переключения карточек, перезапуска вкладки.
 *
 * Черновик — удобство, а не источник правды: результат разговора существует
 * только после метки на сервере. Поэтому любая ошибка хранилища (приватное
 * окно, квота, запрет сайта) глотается — пульт работает как раньше, просто
 * без черновиков. Живёт черновик не дольше суток: вчерашняя анкета на
 * сегодняшнем экране — это уже чужой разговор.
 *
 * Модуль чистый, хранилище передаётся параметром — правила проверяются
 * тестом без браузера.
 */

import { toMskDateTimeLocal } from './callback-time.js';
import type { LeadFormValues } from './lead-form.js';

/** Сколько живёт черновик: дольше — это уже не «недописанная метка», а мусор */
export const LABEL_DRAFT_TTL_MS = 24 * 60 * 60_000;

const KEY_PREFIX = 'cc.desk.labelDraft.';

/** Ровно то, что собирает блок результата: какая метка открыта и что введено */
export type LabelDraft = {
  mode: 'pick' | 'lead' | 'schedule';
  /** Метка, чья форма открыта (заявка или перезвон); null — выбор метки */
  labelId: number | null;
  comment: string;
  lead: LeadFormValues;
  /** Выбранный вариант времени перезвона; 'custom' — своё время в customTime */
  presetKey: string | null;
  customTime: string;
  /**
   * Момент перезвона, который пресет дал при выборе (ISO); null — пресет не
   * выбран или своё время. Пресет относительный: «Завтра в 10:00» наутро
   * значит уже послезавтра, поэтому хранится момент, а не только кнопка.
   */
  callbackAt: string | null;
  /** Когда сохранён, мс epoch: по нему черновик протухает */
  savedAt: number;
};

/** Минимум Storage, который нужен черновику: подменяется в тестах */
export type DraftStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

/**
 * localStorage или null, если его нет. Сам доступ к свойству может бросить
 * (Safari с запретом данных сайта), поэтому он тоже в try.
 */
export function browserDraftStorage(): DraftStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function labelDraftKey(userId: string, assignmentId: string): string {
  return `${KEY_PREFIX}${userId}.${assignmentId}`;
}

/** Пустая форма не хранится: иначе каждый просмотр карточки оставлял бы запись */
export function isEmptyLabelDraft(draft: Omit<LabelDraft, 'savedAt'>, emptyLead: LeadFormValues): boolean {
  return (
    draft.mode === 'pick' &&
    draft.comment.trim() === '' &&
    draft.presetKey === null &&
    draft.customTime === '' &&
    JSON.stringify(draft.lead) === JSON.stringify(emptyLead)
  );
}

export function saveLabelDraft(
  storage: DraftStorage | null,
  userId: string,
  assignmentId: string,
  draft: LabelDraft,
): void {
  if (storage === null) return;
  try {
    storage.setItem(labelDraftKey(userId, assignmentId), JSON.stringify(draft));
  } catch {
    // квота или запрет хранилища — черновик просто не переживёт F5
  }
}

/**
 * Время перезвона из черновика для формы (ревью 28.09.2026). Относительный
 * пресет не возвращается кнопкой: «Завтра в 10:00», выбранный вчера вечером,
 * сегодня молча сдвинул бы перезвон на день позже обещанного. Вместо кнопки —
 * тот же момент своим временем: если он уже прошёл, это поймает проверка
 * «Это время уже прошло» на отправке. Момента нет (битый черновик) — время
 * выбирается заново.
 */
export function restoredCallbackChoice(
  draft: Pick<LabelDraft, 'presetKey' | 'customTime' | 'callbackAt'>,
): { presetKey: string | null; customTime: string } {
  if (draft.presetKey === null || draft.presetKey === 'custom') {
    return { presetKey: draft.presetKey, customTime: draft.customTime };
  }
  const at = draft.callbackAt === null ? Number.NaN : Date.parse(draft.callbackAt);
  if (Number.isNaN(at)) return { presetKey: null, customTime: draft.customTime };
  return { presetKey: 'custom', customTime: toMskDateTimeLocal(new Date(at)) };
}

/**
 * Черновик этой карточки или null. Протухший и битый удаляются сразу:
 * показывать их нельзя, а хранить незачем.
 */
export function loadLabelDraft(
  storage: DraftStorage | null,
  userId: string,
  assignmentId: string,
  now: number,
): LabelDraft | null {
  if (storage === null) return null;
  const key = labelDraftKey(userId, assignmentId);
  try {
    const raw = storage.getItem(key);
    if (raw === null) return null;
    const draft = parseDraft(raw);
    if (draft === null || now - draft.savedAt > LABEL_DRAFT_TTL_MS) {
      storage.removeItem(key);
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

export function clearLabelDraft(storage: DraftStorage | null, userId: string, assignmentId: string): void {
  if (storage === null) return;
  try {
    storage.removeItem(labelDraftKey(userId, assignmentId));
  } catch {
    // хранилище недоступно — удалять и нечего
  }
}

/**
 * Уборка: удаляет черновики старше суток (любого диспетчера на этом
 * компьютере) и черновики этого диспетчера по карточкам, которых сервер больше
 * не держит открытыми (размечена в другой вкладке, отозвана, протухла).
 * openIds = null — список карточек неизвестен, чистится только возраст.
 */
export function purgeLabelDrafts(
  storage: DraftStorage | null,
  userId: string,
  openIds: ReadonlySet<string> | null,
  now: number,
): void {
  if (storage === null) return;
  try {
    const ownPrefix = `${KEY_PREFIX}${userId}.`;
    const doomed: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(KEY_PREFIX) !== true) continue;
      const draft = parseDraft(storage.getItem(key) ?? '');
      if (draft === null || now - draft.savedAt > LABEL_DRAFT_TTL_MS) {
        doomed.push(key);
        continue;
      }
      if (openIds !== null && key.startsWith(ownPrefix) && !openIds.has(key.slice(ownPrefix.length))) {
        doomed.push(key);
      }
    }
    // Удаление после обхода: removeItem внутри цикла сдвигает индексы key(i)
    for (const key of doomed) storage.removeItem(key);
  } catch {
    // уборка — не обязанность пульта: не вышло сейчас, выйдет на следующем снапшоте
  }
}

function parseDraft(raw: string): LabelDraft | null {
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null) return null;
    const d = value as Record<keyof LabelDraft, unknown>;
    if (
      (d.mode !== 'pick' && d.mode !== 'lead' && d.mode !== 'schedule') ||
      typeof d.savedAt !== 'number' ||
      typeof d.comment !== 'string' ||
      typeof d.customTime !== 'string' ||
      typeof d.lead !== 'object' ||
      d.lead === null ||
      (d.labelId !== null && typeof d.labelId !== 'number') ||
      (d.presetKey !== null && typeof d.presetKey !== 'string')
    ) {
      return null;
    }
    // Черновик без callbackAt (записан до поля) — момента пресета нет
    return { ...(value as LabelDraft), callbackAt: typeof d.callbackAt === 'string' ? d.callbackAt : null };
  } catch {
    return null;
  }
}
